/**
 * Guest-realm source for the DB Manager reference extension. A string —
 * injected verbatim into the sandboxed iframe's srcdoc (a real bundled
 * extension would ship an equivalent `dist/main.js`). The only parts that go
 * through CRA/babel are the helpers interpolated from ./guest/*.js via
 * Function.prototype.toString (self-contained ES5, unit-tested there).
 *
 * Multi-engine: MySQL/MariaDB (`db:mysql`), PostgreSQL (`db:postgres`) and
 * SQLite (`db:sqlite`), each behind its own capability. Multi-CONNECTION
 * (HeidiSQL-style session manager): any number of simultaneous connections,
 * each with its own tree root, connId, and stored password.
 *
 * Storage model:
 *   sdk.storage 'connections' — array of saved descriptors
 *     { id, name, engine, host, port, user, database, path, remember, createdAt }
 *   secureStorage (hidden Vault items; the OS keychain before 26.10.2026) —
 *     one password per LEGACY connection, key
 *     'conn-<id>-password'; the connection migrated from the legacy
 *     single-connection format keeps id 'default' and the legacy
 *     '<engine>-password' key (see secureKeyFor). The host mirrors both
 *     schemes in capabilities/db.js (dbPasswordKeychainKey /
 *     readSavedDbConnection) — keep them in sync.
 *   Legacy 'connectionConfig' (single object) is migrated on first activate
 *   into a 'connections' entry with id 'default', so host-side connection
 *   liveness and restored tabs keyed on connId 'default' keep working.
 *
 * Tree: ONE ROOT PER CONNECTION. Every node id is namespaced
 * '<connId>|<local-id>' — generated connIds never contain '|', so the
 * connection is always recoverable from the prefix and two servers with
 * identical schema names can't collide. Disconnected connections stay in the
 * tree (click or the plug action connects); connected roots expose
 * refresh/edit/disconnect/remove actions.
 *
 * Publishes a Collections-styled lazy tree via `sdk.tree.publish`/`onExpand`:
 *
 *   mysql:    connection -> Databases -> <db> -> Tables/Views -> <table|view> -> categories
 *   postgres: connection -> Tables/Views (public schema) -> <table|view> -> categories
 *   sqlite:   connection -> Tables/Views -> <table|view> -> categories
 *
 * Views live in a sibling "Views" node per database/schema; a view node shows
 * only a Columns category and opens in the same host grid as a table (labeled
 * "(view)"). Table nodes carry Show CREATE (DDL viewer rendered in the panel)
 * and Design table (columns modal prefilled -> diffed into ALTER statements,
 * confirmed in the panel before executing) hover actions; view nodes carry
 * Show CREATE only.
 *
 * Each level below the connection root is lazy (queried only on first
 * expand). Selecting a table opens a host-rendered results tab
 * (`sdk.ui.openTable`), which runs its own engine-aware queries.
 */
import { guestCoreSource } from './guestSource/guestCore';
import { guestTreeSource } from './guestSource/guestTree';
import { guestConnectSource } from './guestSource/guestConnect';
import { guestConnectionModalsSource } from './guestSource/guestConnectionModals';
import { guestSchemaEditingSource } from './guestSource/guestSchemaEditing';
import { guestLifecycleSource } from './guestSource/guestLifecycle';

// One string, cut at top-level guest functions into ./guestSource/* (21.10.2026).
// The parts are verbatim template text, so the joined string is unchanged.
export const dbManagerEntrySource = guestCoreSource
  + guestTreeSource
  + guestConnectSource
  + guestConnectionModalsSource
  + guestSchemaEditingSource
  + guestLifecycleSource;
